import { getGatedIdacBackendUrl } from '../utilities/env';
import { Action, SignupErrorCode, SignupFieldError, ThunkAction, URLParameters } from '../types';
import queryString from 'query-string';
import { getMicrobrandingFromUrl } from '../utilities/applications';
import { ChallengeResponse } from '../contexts/recaptcha';
import { ChallengeRequest, toChallengeRequest } from '../utilities/recaptcha';
import { getCastleRequestToken } from '../castle';
import { captureTaggedException } from '../utilities/analytics/error-reporting';
import { restoreOriginalRedirectUrlParams } from '../utilities/redirect-url-params';

export const reset = (): Action => ({
  type: 'SIGNUP_RESET',
});

export const signupRequest = (): Action => ({
  type: 'SIGNUP_REQUEST',
});

export const signupRequestSucceeded = (redirectTo: string): Action => ({
  type: 'SIGNUP_REQUEST_SUCCESS',
  redirectTo,
});

export const signupRequestFailed = (error: SignupErrorCode): Action => ({
  type: 'SIGNUP_REQUEST_FAILED',
  error,
});

export const signupRequestFailedWithRedirect = (
  error: SignupErrorCode,
  redirectTo: string
): Action => ({
  type: 'SIGNUP_REQUEST_FAILED_WITH_REDIRECT',
  error,
  redirectTo,
});

export const showFieldErrors = (errors: SignupFieldError): Action => ({
  type: 'SIGNUP_SHOW_FIELD_ERRORS',
  errors,
});

export interface SignupParams {
  email: string;
  challengeResponse?: ChallengeResponse | null;
  privacyPolicyText: string;
}

export interface SignupPayload {
  'challenge-response'?: ChallengeRequest | null;
  email: string;
  token?: string;
  productHint?: string;
  display: string | null;
  nativeHandoffSignupToken?: string;
  castleToken?: string | null;
}

export const signupCaptchaFailure = (isOnline: boolean) =>
  signupRequestFailed(isOnline ? 'captcha' : 'request_error');

export const signup =
  (
    signupParams: SignupParams,
    location: Location = window.location,
    options?: {
      addParamsToContinueUrl?: (_: { fullRedirectUrl: URL; continueUrl: URL }) => URLSearchParams;
    }
  ): ThunkAction =>
  async (dispatch, getState) => {
    const { email, challengeResponse } = signupParams;

    const { display, nativeHandoff: nativeHandoffSignupToken } = queryString.parse(
      location.search
    ) as URLParameters;
    const {
      cobranding: { productHint },
    } = getState();

    const microbranding = getMicrobrandingFromUrl(location);

    const qs =
      microbranding && Object.keys(microbranding).length
        ? `?${queryString.stringify(microbranding)}`
        : '';

    const castleToken = await getCastleRequestToken([
      'capability-group--signup',
      'capability-name--signup',
    ]);

    dispatch(signupRequest());

    // TODO: confirm whether we should be including privacy policy text in the payload
    // https://hello.jira.atlassian.cloud/browse/KIRBY-7103
    const payload: SignupPayload = {
      'challenge-response': toChallengeRequest(challengeResponse),
      email,
      productHint,
      display: display || null,
      castleToken,
      // this token is only intended to be used by native apps (e.g. Dia)
      ...(nativeHandoffSignupToken && { nativeHandoffSignupToken }),
    };

    let response: Response;
    try {
      const path = getGatedIdacBackendUrl(`/rest/signup${qs}`);
      response = await fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    } catch (networkError) {
      dispatch(signupRequestFailed('request_error'));
      return;
    }

    try {
      const { fieldErrors, uiErrors, redirectTo } = await response.json();
      if (redirectTo) {
        const fullRedirectUrl = new URL(redirectTo);
        const redirectUrlParams = new URLSearchParams(fullRedirectUrl.search);
        const continueUrlFromParams = redirectUrlParams.get('continue');
        const isInvalidNativeSignup = redirectUrlParams.get('errorCode') === 'invalidNativeSignup';

        // SIS rebuilds this redirect from a fixed set of params, so restore the original
        // signup page params it dropped to avoid losing cobranded/base flow context on retry.
        if (isInvalidNativeSignup) {
          const updatedRedirectTo = restoreOriginalRedirectUrlParams({
            fullRedirectUrlValue: redirectTo,
            originalParams: new URLSearchParams(location.search),
            excludedParams: ['nativeHandoff'],
          });

          dispatch(signupRequestFailedWithRedirect('invalidNativeSignup', updatedRedirectTo));
          return;
        }

        if (options?.addParamsToContinueUrl && continueUrlFromParams) {
          const continueUrl = new URL(continueUrlFromParams);

          const paramsToAdd = options.addParamsToContinueUrl({
            fullRedirectUrl,
            continueUrl,
          });

          const updatedRedirectTo = updateRedirectContinueUrlParams({
            fullRedirectUrlValue: redirectTo,
            continueUrlValue: continueUrlFromParams,
            paramsToAdd,
          });

          dispatch(signupRequestSucceeded(updatedRedirectTo));
          return;
        }

        dispatch(signupRequestSucceeded(redirectTo));
      } else {
        if (uiErrors && uiErrors.length) {
          dispatch(signupRequestFailed(uiErrors[0].key || 'unknown'));
        } else if (fieldErrors && Object.keys(fieldErrors).length) {
          dispatch(showFieldErrors(fieldErrors));
        } else {
          dispatch(signupRequestFailed('unknown'));
        }
      }
    } catch (error) {
      captureTaggedException(error, {
        capabilities: ['capability-group--signup', 'capability-name--signup'],
        extraProperties: {
          description: 'Failed to handle the signup response',
          responseStatus: response.status,
        },
      });
      dispatch(signupRequestFailed('unknown'));
    }
  };

type UpdateRedirectContinueUrlParams = {
  fullRedirectUrlValue: string;
  continueUrlValue: string;
  paramsToAdd: URLSearchParams;
};

/**
 * This function allows additive changes only to continue URL search params.
 *
 * The full redirect URL often has a continue URL defined in its search params, usually pointing to
 * a destination product, like Loom. This continue URL, in turn, may have search params of its own.
 *
 * In some cases, consumers of the signup function may want to include certain additional params
 * inside the continue URL, based on what happened during the signup flow. This is useful to be able
 * to make decisions once the user lands on the product's domain.
 *
 * For example, a product may want to include a param to indicate that the user attempted to sign
 * up, but was redirected to /login on IDAC because they already have an account. They can include a
 * param in the continue URL to indicate that this was the case.
 */
const updateRedirectContinueUrlParams = ({
  fullRedirectUrlValue,
  paramsToAdd,
  continueUrlValue,
}: UpdateRedirectContinueUrlParams): string => {
  const fullRedirectUrl = new URL(fullRedirectUrlValue);
  const redirectUrlParams = new URLSearchParams(fullRedirectUrl.search);
  const continueUrl = new URL(continueUrlValue);

  // update the params of the continue URL
  const existingParams = new URLSearchParams(continueUrl.search);
  const combinedParams = new URLSearchParams([...existingParams, ...paramsToAdd]);
  continueUrl.search = combinedParams.toString();

  // update the continue param of the full redirect URL
  redirectUrlParams.set('continue', continueUrl.toString());
  fullRedirectUrl.search = redirectUrlParams.toString();

  return fullRedirectUrl.toString();
};
